import React, { useCallback, useEffect, useMemo, useState } from "react";
import { usePost } from "@/apiHooks/HttpFunctions";
import RenderToast from "@/components/RenderToast";
import { Col, Row } from "react-bootstrap";
import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import { useFormik } from "formik";
import classes from "@/Modals/BaseModal.module.css";
import Input from "@/components/Input/Input";
import useFormApiState from "@/CustomHooks/useFormApiState";
import getAdminAdjustmentSchema from "@/Modals/AdminAdjustmentAction/schema";

function AdminAdjustmentActionModal({
  headerText,
  isOpen,
  endpointUrl,
  onCancel,
  onSuccess,
  requireReason = true,
  allowReference = true,
  allowAttachments = true,
  extraFormData = {},
}) {
  const initialFormData = useMemo(
    () => ({
      reason: "",
      reference_id: "",
      attachments: [],
    }),
    []
  );
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    setFormData((prevData) => ({ ...prevData, ...extraFormData }));
  }, [extraFormData]);

  const adminActionPost = usePost({
    apiURL: endpointUrl,
    isFormData: true,
    onSuccess: (data) => onSubmitSuccess(data.message, onSuccess),
    onError: (error) => {
      RenderToast({
        type: "error",
        message: error.response.data.errors.error ?? "Something went wrong.",
      });
    },
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [adminActionPost.isPending],
    [adminActionPost.error]
  );

  const handleSubmit = async (values) => {
    const formData = new FormData();
    formData.append("reason", values.reason);
    formData.append("reference_id", values.reference_id);
    for (let file of values["attachments"]) {
      formData.append("attachments[]", file);
    }
    adminActionPost.mutate(formData);
  };

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: getAdminAdjustmentSchema(requireReason),
    initialValues: formData,
    onSubmit: handleSubmit,
  });

  const onSubmitSuccess = useCallback(
    (message, callback = null) => {
      setFormData(initialFormData);
      formik.resetForm();
      RenderToast({ type: "success", message: message ?? "Success" });
      if (callback) {
        callback();
      }
    },
    [formik, initialFormData]
  );

  const onFileChange = useCallback(
    (e) => {
      formik.setFieldValue("attachments", e.target.files);
    },
    [formik]
  );

  return (
    <BasicModal
      header={headerText}
      show={isOpen}
      onHide={onCancel}
      saveLoading={saveIsPending}
      formik={formik}
      saveLabel={"Proceed"}
    >
      <Row>
        <Col md={12}>
          <p className="mb-1 font-bold">Please enter details for this action</p>
        </Col>
      </Row>
      {requireReason && (
        <Row>
          <Col md={12}>
            <BasicInput
              formik={formik}
              fieldName={"reason"}
              saveError={saveError}
              label="Reason*"
              placeholder="Enter reason"
              type="textarea"
              rows={2}
            />
          </Col>
        </Row>
      )}
      {allowReference && (
        <Row>
          <Col md={12}>
            <BasicInput
              formik={formik}
              fieldName={"reference_id"}
              saveError={saveError}
              label="Reference ID"
              required={false}
            />
          </Col>
        </Row>
      )}
      {allowAttachments && (
        <Row>
          <Col md={12}>
            <Input
              type="file"
              value={formik.values.attachments}
              onChange={onFileChange}
              labelClass={classes.label}
              label="Attachments"
              className={classes.inputDiv}
              inputContainerClass={classes.input}
              errorTextClass={classes.errorText}
              multiple
              accept="image/*, application/pdf"
              required={false}
            />
          </Col>
        </Row>
      )}
    </BasicModal>
  );
}

export default AdminAdjustmentActionModal;
